import {useMemo, useState, useEffect} from 'react';
import {useParams, useHistory, useLocation} from 'react-router-dom';
import Tabs from '../interfaces/Tabs';

//#region Constants
const tabDictionary: Record<string, string> = {
  [Tabs.CUSTOM]: Tabs.CUSTOM,
  [Tabs.NA]: Tabs.NA,
  [Tabs.EMEA]: Tabs.EMEA,
  [Tabs.GC]: Tabs.GC,
  [Tabs.APLA]: Tabs.APLA,
};
//#endRegion Constants

//#region Helper Functions
const parseTabParams = (tabParams: string) => {
  const lowerCaseTabParams = tabParams?.toLowerCase() || Tabs.CUSTOM;
  const selectedTab = tabDictionary[lowerCaseTabParams] || Tabs.CUSTOM;

  return selectedTab as Tabs;
};

function useQuery() {
  const {search} = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}
//#endRegion Helper Functions

function useFilters() {
  const {tab: tabParams} = useParams<any>();
  const parsedTabParams = useMemo(() => parseTabParams(tabParams), [tabParams]);

  let query = useQuery();
  let history = useHistory();

  //#region handles
  const handleFilterChange = (selectedFilters: string[], category: string) => {
    let allParams: Record<string, string | null> = {
      geo: query.get('geo'),
      brand: query.get('brand'),
      siteType: query.get('siteType'),
      storeConcept: query.get('storeConcept'),
      siteStatus: query.get('siteStatus'),
      deviceType: query.get('deviceType'),
      nvsnso: query.get('nvsnso'),
    };

    if (allParams[category] !== null) {
      const categoryArray = allParams[category]?.split(',') || [];
      const newCategoryArray = [...categoryArray];
      selectedFilters.forEach(filter => {
        if (!newCategoryArray.includes(filter)) {
          newCategoryArray.push(filter);
        } else {
          newCategoryArray.splice(newCategoryArray.indexOf(filter), 1);
        }
      });

      allParams[category] = newCategoryArray.join(',');
    } else {
      allParams[category] = selectedFilters.join(',');
    }

    const urlParams = [];

    for (const [key, value] of Object.entries(allParams)) {
      if (value) {
        urlParams.push(`${key}=${value}`);
      }
    }

    const toUrl = urlParams.length > 0 ? `?${urlParams.join('&')}` : '';

    history.push(`/device-inventory/${parsedTabParams}${toUrl}`);
  };

  const handleClearFilters = () => {
    history.push(`/device-inventory/${parsedTabParams}`);
  };

  const handleTabChange = (tab: Tabs) => {
    const tabUrl = parseTabParams(tab);
    history.push(`/device-inventory/${tabUrl}`);
  };

  //#endRegion handles

  const [geo, setGeo] = useState<string[]>([]);
  const [brand, setBrand] = useState<string[]>([]);
  const [siteType, setSiteType] = useState<string[]>([]);
  const [storeConcept, setStoreConcept] = useState<string[]>([]);
  const [siteStatus, setSiteStatus] = useState<string[]>([]);
  const [deviceType, setDeviceType] = useState<string[]>([]);
  const [nvsnso, setNvsnso] = useState<string[]>([]);

  useEffect(() => {
    const geoQuery = query.get('geo');
    const brandQuery = query.get('brand');
    const siteTypeQuery = query.get('siteType');
    const conceptQuery = query.get('storeConcept');
    const siteStatusQuery = query.get('siteStatus');
    const deviceTypeQuery = query.get('deviceType');
    const nvsnsoQuery = query.get('nvsnso');

    const geo: string[] = geoQuery ? geoQuery.split(',') : [];
    const brand: string[] = brandQuery ? brandQuery.split(',') : [];
    const siteType: string[] = siteTypeQuery ? siteTypeQuery.split(',') : [];
    const concept: string[] = conceptQuery ? conceptQuery.split(',') : [];
    const siteStatus: string[] = siteStatusQuery ? siteStatusQuery.split(',') : [];
    const deviceType: string[] = deviceTypeQuery ? deviceTypeQuery.split(',') : [];
    const nvsnso: string[] = nvsnsoQuery ? nvsnsoQuery.split(',') : [];

    setGeo(geo);
    setBrand(brand);
    setSiteType(siteType);
    setStoreConcept(concept);
    setSiteStatus(siteStatus);
    setDeviceType(deviceType);
    setNvsnso(nvsnso);
  }, [query]);

  return {
    handleFilterChange,
    handleClearFilters,
    handleTabChange,
    tab: parsedTabParams,
    geo,
    brand,
    siteType,
    storeConcept,
    siteStatus,
    deviceType,
    nvsnso,
  };
}

export default useFilters;
